/**
 * The data shapes NovaCompanionChatComponent renders and exchanges with
 * NovaCompanionChatService. Ported from the confirmed design handoff
 * (`nova-companion/companion-chat/companion-chat.js`, FLIP-173) — see that file's
 * conversation/tag-parsing logic for the source of truth this mirrors.
 */

export type ChatRole = "user" | "assistant";

export interface ChatMessage {
  role: ChatRole;
  /** Display text - tags like [[NAV:X]] / [[SAFETY_ESCALATION_TRIGGERED]] already stripped. */
  content: string;
}

/** Destinations the handoff's [[NAV:X]] tag can name. Surfaced as the handoff's navtoast,
 *  which we make clickable: pressing it closes the chat and routes via NOVA_NAV_ROUTES.
 *  (The handoff's own toast was static - real routing is ours.) */
export type NovaNavTarget =
  | "Score"
  | "Plan"
  | "Sanctuary"
  | "Learn"
  | "Community"
  // Home and Settings are ask-only: the prompt forbids Nova offering them as a suggestion,
  // so they arrive when someone said "take me to settings", never off her own bat.
  | "Home"
  | "Settings";

export const NOVA_NAV_LABELS: Record<NovaNavTarget, string> = {
  Score: "Score",
  Plan: "Plan",
  Sanctuary: "Sanctuary",
  Learn: "Learn",
  Community: "Community",
  Home: "Home",
  Settings: "Settings",
};

/** Routes corresponding to each navigation target for app navigation. */
export const NOVA_NAV_ROUTES: Record<NovaNavTarget, string> = {
  Score: "/score",
  Plan: "/plan-home",
  Sanctuary: "/sanctuary",
  Learn: "/learn",
  Community: "/community",
  // Both are children of the guarded "" route (app-routing.module.ts). Home is the landing
  // page rather than "", which only redirects here - the same reason Plan is "/plan-home".
  Home: "/landing-page",
  Settings: "/settings",
};

/**
 * The routes a server-resolved link may open, and the target each stands for. A route the server
 * names that is not here is a no-op rather than a navigation - the same posture `onNavChipClicked`
 * takes for an unknown tag - so a link the client does not know can never send anyone somewhere
 * unvalidated. /help is deliberately absent: the catalogue lists it but the app has no such route.
 */
export const NOVA_LINKABLE_ROUTES: Readonly<Record<string, NovaNavTarget>> = {
  "/settings": "Settings",
  "/community": "Community",
};

/**
 * A navigation target the SERVER resolved to a routable id.
 *
 * Mirrors `NovaNavigationTarget` in `NovaChatModels.cs`, and arrives on the final stream frame.
 * Its presence is the validation: the model names a lesson by title, the server matches that title
 * against the catalogue it was shown, and only a real match produces one of these. So the client
 * never decides whether a title is genuine - it either has an id to route to or it does not.
 *
 * Absent on most turns (no tag, a bare [[NAV:Learn]], or a title that did not resolve), and all
 * three cases mean the same thing here: route to the section.
 */
export interface NovaResolvedNav {
  /** The app section, or null for a link to a page outside the five sections (see `route`). */
  area: string | null;
  /**
   * A client route such as "/settings", set instead of `area` for a page outside the five
   * sections. Only ever a route the server took from its own allowlist - and checked again here
   * against `NOVA_LINKABLE_ROUTES` before anything routes on it.
   */
  route?: string | null;
  /** "lesson", "chapter", or "section" for a link to a page rather than something inside one. */
  kind: string;
  /** Lesson or chapter id. Never persisted - a content reseed reassigns these. */
  targetId: number;
  /** The title that matched, used to label the toast with the destination. */
  title: string;
}

/** Result of parsing a raw assistant reply for its [[NAV:X]] tag. `nav` is the raw tag
 *  content, not yet validated against NovaNavTarget - the caller looks it up in
 *  NOVA_NAV_LABELS and falls back to the raw value, mirroring the handoff's
 *  `labels[tool] || tool`. */
export interface ParsedNav {
  clean: string;
  nav: string | null;
  /**
   * The title named inside a TARGETED tag, where there was one.
   *
   * Parsed here only so the toast can be labelled with the destination when the server's resolved
   * target is unavailable - restored history being the case that matters, since those turns are
   * replayed from stored text with no stream frame behind them. It is NOT a routing input: an
   * unresolved title routes to the section, never to a guessed id.
   */
  navTitle: string | null;
}

/**
 * Where a resolved target actually routes.
 *
 * A lesson has no URL of its own - the lesson modal opens from a `lesson` query param on whichever
 * Learn child route is showing, which is why this returns params rather than a deeper path. The
 * shell searches every chapter for the id and silently ignores one it cannot find, so a stale id
 * degrades to the Learn homepage without a check here. `src` tells the shell how the lesson was
 * reached, alongside the existing "insight" source.
 */
export function novaResolvedNavRoute(
  resolved: NovaResolvedNav
): { route: string; queryParams?: Record<string, string | number> } | null {
  // A link to a page outside the five sections carries a route rather than an area, and is only
  // followed if the client knows it.
  if (resolved?.route) {
    return NOVA_LINKABLE_ROUTES[resolved.route] ? { route: resolved.route } : null;
  }

  if (!resolved || resolved.area !== "Learn" || !resolved.targetId) return null;

  if (resolved.kind === "lesson") {
    return {
      route: NOVA_NAV_ROUTES.Learn,
      queryParams: { lesson: resolved.targetId, src: "nova" },
    };
  }

  if (resolved.kind === "chapter") {
    return { route: `${NOVA_NAV_ROUTES.Learn}/${resolved.targetId}` };
  }

  return null;
}

/**
 * The toast target a server-resolved link stands for: its area, or for a route-keyed link the
 * target that route belongs to. Null for a link that names neither, or a route the client does not
 * link - which is what lets `pushNavToast` labelling and `onNavChipClicked` routing agree.
 */
export function novaNavTargetOfResolved(resolved: NovaResolvedNav | null | undefined): string | null {
  if (!resolved) return null;
  if (resolved.route) return NOVA_LINKABLE_ROUTES[resolved.route] ?? null;
  return resolved.area ?? null;
}

/** Result of parsing a raw assistant reply for its [[SAFETY_ESCALATION_TRIGGERED]] tag. */
export interface ParsedSafetyEscalation {
  clean: string;
  triggered: boolean;
}

/** Current Safety Buffer band - drives the chat header's colour treatment. */
export type BufferBand = "green" | "amber" | "red" | "no-score";

/**
 * One stored turn from a PREVIOUS conversation, as returned by GetChatHistory for the scrollback.
 *
 * Deliberately a different type from ChatMessage. These are read-only history rendered above the
 * current exchange, and they must never end up in the array posted to SendMessage - keeping the
 * types apart is what makes that mistake visible rather than silent.
 */
export interface ChatHistoryTurn {
  role: ChatRole;
  content: string;
  /** ISO-8601 UTC. Drives the relative date on conversation dividers. */
  occurredAt: string;
  /** Null for turns written before conversations existed; those group by day instead. */
  conversationId: string | null;
}

/**
 * A past conversation, ready to render: its turns plus the heading shown above them.
 *
 * Built client-side from the flat turn list, because the grouping is presentational - the server
 * says which conversation each turn belongs to, and the client decides how a reader should see
 * that.
 */
export interface ChatHistoryGroup {
  /** Conversation id, or a synthetic day key for legacy turns with none. */
  key: string;
  /** Relative heading: "Earlier today", "Yesterday", "Last Tuesday", or a date. */
  heading: string;
  turns: ChatHistoryTurn[];
}
